// Gift With Purchase -- auto-add/remove complimentary gift variant -- SG 20260331

const GWP_CONFIG = {
  qualifyingCollectionHandle: 'collab-miffy-gwp',
  giftCollectionHandle: 'collab-miffy-toys',
  giftPropertyKey: '_giftPromo',
  giftPropertyValue: 'miffy-gwp',
  maxGiftsPerOrder: 1,
  debounceMs: 150,
}

const CAP_REACHED_KEY = 'gwp-miffy-cap-reached'

interface GiftCandidate {
  productId: number
  variantId: number
}

let _qualifyingProductIds: number[] | null = null
let _giftCandidates: GiftCandidate[] | null = null
let _giftCheckInProgress = false
let _debounceTimer: ReturnType<typeof setTimeout> | null = null
let _lastItemCount = -1
let _lastHadGwpRelevance = true // assume relevant on first run -- SG 20260414

// Pre-warm caches at init so they're ready before first cart event -- SG 20260413
function preWarmCaches(): void {
  fetchQualifyingProductIds()
  fetchGiftCandidates()
}

// Fetch qualifying product IDs from collection, cache for session -- SG 20260331
async function fetchQualifyingProductIds(): Promise<number[]> {
  if (_qualifyingProductIds) return _qualifyingProductIds
  try {
    const res = await fetch(
      `/collections/${GWP_CONFIG.qualifyingCollectionHandle}/products.json?limit=250`
    )
    if (!res.ok) {
      console.warn('[GWP] Failed to fetch qualifying collection:', res.status)
      return []
    }
    const data = await res.json()
    _qualifyingProductIds = (data.products || []).map((p: any) => p.id)
    return _qualifyingProductIds!
  } catch (err) {
    console.warn('[GWP] Error fetching qualifying collection:', err)
    return []
  }
}

// Fetch gift candidates from collection -- filters to available, shuffled -- SG 20260413
async function fetchGiftCandidates(): Promise<GiftCandidate[]> {
  if (_giftCandidates) return _giftCandidates
  try {
    const res = await fetch(
      `/collections/${GWP_CONFIG.giftCollectionHandle}/products.json?limit=50`
    )
    if (!res.ok) {
      console.warn('[GWP] Failed to fetch gift collection:', res.status)
      return []
    }
    const data = await res.json()
    const candidates: GiftCandidate[] = (data.products || [])
      .filter((p: any) => p.variants?.length && p.variants[0].available)
      .map((p: any) => ({ productId: p.id, variantId: p.variants[0].id }))

    // Shuffle so each session gets a random order -- SG 20260413
    for (let i = candidates.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [candidates[i], candidates[j]] = [candidates[j], candidates[i]]
    }

    _giftCandidates = candidates
    return _giftCandidates
  } catch (err) {
    console.warn('[GWP] Error fetching gift collection:', err)
    return []
  }
}

// All gift product IDs from the candidates list -- SG 20260413
function getGiftProductIds(): number[] {
  return (_giftCandidates || []).map(c => c.productId)
}

// Match gift by hidden property (GWP-added) -- SG 20260331
function isGwpGiftLine(item: any): boolean {
  return item.properties?.[GWP_CONFIG.giftPropertyKey] === GWP_CONFIG.giftPropertyValue
}

// Match any gift product from the collection -- SG 20260413
function isGiftProduct(item: any): boolean {
  return getGiftProductIds().includes(item.product_id)
}

// Check qualifying status against cached IDs -- synchronous when cache warm -- SG 20260413
function hasQualifyingProduct(cart: any, qualifyingIds: number[]): boolean {
  if (!qualifyingIds.length) return false
  return cart.items.some(
    (item: any) => !isGiftProduct(item) && qualifyingIds.includes(item.product_id)
  )
}

// Try adding a gift -- walks candidates until one succeeds or all fail -- SG 20260413
async function tryAddGift(candidates: GiftCandidate[]): Promise<any> {
  for (const candidate of candidates) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: candidate.variantId,
          quantity: 1,
          properties: { [GWP_CONFIG.giftPropertyKey]: GWP_CONFIG.giftPropertyValue },
        }],
      }),
    })
    if (res.ok) return res.json()
    if (res.status !== 422) return null
    // 422 = out of stock for this candidate, try next -- SG 20260413
  }
  // All candidates exhausted -- SG 20260413
  sessionStorage.setItem(CAP_REACHED_KEY, 'true')
  announceGiftStatus('This complimentary gift is no longer available')
  return null
}

async function removeGiftFromCart(itemKey: string): Promise<void> {
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: itemKey, quantity: 0 }),
  })
}

function announceGiftStatus(message: string) {
  const el = document.getElementById('gwp-status')
  if (el) el.textContent = message
}

async function fetchCart(): Promise<any> {
  const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
  return res.json()
}

// Core GWP logic -- SG 20260413
async function checkAndAddGift(itemCount?: number): Promise<void> {
  if (_giftCheckInProgress) return
  if (sessionStorage.getItem(CAP_REACHED_KEY) === 'true') return

  // Gate: skip /cart.js fetch if cart unchanged and last check had nothing GWP-relevant -- SG 20260414
  if (itemCount !== undefined && itemCount === _lastItemCount && !_lastHadGwpRelevance) return

  _giftCheckInProgress = true

  try {
    const [cart, qualifyingIds, candidates] = await Promise.all([
      fetchCart(),
      fetchQualifyingProductIds(),
      fetchGiftCandidates(),
    ])

    _lastItemCount = cart.item_count ?? -1

    const gwpGift = cart.items.find((item: any) => isGwpGiftLine(item))
    const manualGift = cart.items.find((item: any) => !isGwpGiftLine(item) && isGiftProduct(item))
    const qualifying = hasQualifyingProduct(cart, qualifyingIds)

    _lastHadGwpRelevance = qualifying || !!gwpGift || !!manualGift

    if (gwpGift && manualGift) {
      // Both exist -- remove the GWP copy; BXGY handles the manual one -- SG 20260413
      await removeGiftFromCart(gwpGift.key)
      refreshCartUI()
    } else if (qualifying && !gwpGift && !manualGift) {
      // No gift in cart -- try adding from shuffled candidates -- SG 20260413
      if (!candidates.length) return
      const addResult = await tryAddGift(candidates)
      if (!addResult) return
      announceGiftStatus('Complimentary Miffy Eco Plush Toy added to your cart')
      refreshCartUI()
    } else if (qualifying && manualGift) {
      // Customer added the toy manually -- BXGY handles pricing, nothing to do -- SG 20260413
    } else if (!qualifying && gwpGift) {
      // Only auto-remove gifts we added -- leave manually-added ones alone -- SG 20260413
      await removeGiftFromCart(gwpGift.key)
      announceGiftStatus('Complimentary gift removed from your cart')
      refreshCartUI()
    }
  } catch (err) {
    console.warn('[GWP] Error during gift check:', err)
  } finally {
    _giftCheckInProgress = false
  }
}

function refreshCartUI() {
  const previewCart = document.querySelector('preview-cart') as any
  if (previewCart?.updateCartHTML) {
    previewCart.updateCartHTML()
  }
}

export function scheduleGiftCheck(itemCount?: number) {
  if (_debounceTimer) clearTimeout(_debounceTimer)
  _debounceTimer = setTimeout(() => checkAndAddGift(itemCount), GWP_CONFIG.debounceMs)
}

// Auto-initialize: pre-warm caches, run GWP check on load and cart mutations -- SG 20260413
function initGiftWithPurchase() {
  preWarmCaches()
  scheduleGiftCheck()
  document.addEventListener('cart:updated', ((e: CustomEvent) => {
    scheduleGiftCheck(e.detail?.itemCount)
  }) as EventListener)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGiftWithPurchase)
} else {
  initGiftWithPurchase()
}
